// Environment / data-plane configuration for the shared-identity test/live toggle.
//
// Auth/identity calls always go to the constant production authority; business calls (and realtime/WS,
// Stripe) go to the toggled data plane. The active env is persisted in localStorage; switching it
// triggers a full page reload (see the App Info page), which is the simplest way to refresh all
// plane-specific state and re-initialize the Stripe singleton without a re-login (the token persists).

export type EnvName = 'production' | 'sandbox' | 'local';

interface EnvConfig {
  coreApi: string;
  realtimeEngine: string;
  ws: string;
  siteUrl: string;
  stripeKey: string;
}

const KNOWN: Record<EnvName, EnvConfig> = {
  production: {
    coreApi: 'https://api.snagg.live',
    realtimeEngine: 'https://realtime-api.snagg.live',
    ws: 'wss://realtime-api.snagg.live',
    siteUrl: 'https://www.snagg.live',
    stripeKey: 'pk_live_51T80ljIUxvNxjZuC4vCg9YFuUky3ST824F0jkoP0hyLaWQ3YwYa3ywLkhE09Zfy0yPRy3p7UzyoZk1RtyIZAtLWq00xiZlCuxE',
  },
  sandbox: {
    coreApi: 'https://sandbox-api.snagg.live',
    realtimeEngine: 'https://sandbox-realtime-api.snagg.live',
    ws: 'wss://sandbox-realtime-api.snagg.live',
    siteUrl: 'https://sandbox.snagg.live',
    stripeKey: 'pk_test_51SwnxsEp7Yrau5hJhBVUK4sqYag2JpJCgTK0cdsuW76CiWAl8skVe4uTLTMg06vMsYvoIb0mjcqYrS68eQ6ryCIl00SiqvfT4p',
  },
  local: {
    coreApi: import.meta.env.VITE_CORE_API_URL || 'http://localhost:8081',
    realtimeEngine: import.meta.env.VITE_REALTIME_ENGINE_URL || 'http://localhost:8080',
    ws: import.meta.env.VITE_WS_URL || 'ws://localhost:8080',
    siteUrl: import.meta.env.VITE_SITE_URL || 'http://localhost:3000',
    stripeKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',
  },
};

const STORAGE_KEY = 'snagg_env';

// Which env this build defaults to, derived from the build-time API URL.
function buildDefaultEnv(): EnvName {
  const url = import.meta.env.VITE_CORE_API_URL || '';
  if (url.includes('sandbox')) return 'sandbox';
  if (url.includes('://api.snagg.live')) return 'production';
  return 'local';
}

function readStoredEnv(): EnvName | null {
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'production' || v === 'sandbox' || v === 'local' ? v : null;
}

let activeEnv: EnvName = readStoredEnv() ?? buildDefaultEnv();

export function getActiveEnv(): EnvName {
  return activeEnv;
}

/** Persist the active env. Callers should reload the page afterwards to refresh plane-specific state. */
export function setActiveEnv(env: EnvName): void {
  activeEnv = env;
  localStorage.setItem(STORAGE_KEY, env);
}

// Auth/identity → constant production authority (except local dev, which follows the active env).
export function getAuthApiUrl(): string {
  return activeEnv === 'local' ? KNOWN.local.coreApi : KNOWN.production.coreApi;
}

// Business + realtime + WS + Stripe → toggled data plane.
export function getDataApiUrl(): string {
  return KNOWN[activeEnv].coreApi;
}
export function getRealtimeUrl(): string {
  return KNOWN[activeEnv].realtimeEngine;
}
export function getWsUrl(): string {
  return KNOWN[activeEnv].ws;
}
export function getSiteUrl(): string {
  return KNOWN[activeEnv].siteUrl;
}
export function getStripeKey(): string {
  return KNOWN[activeEnv].stripeKey;
}
